import { topBarHeight } from '@signing-page/tracking/constants/tracking';
import { IRecipient } from '@signing-page/documents/types/recipient';
import { ERecipientStatus } from '@signing-page/recipient/types/enums/recipient-status';
import { EDocumentPageType } from '@lib-shared/enums/document-page-type';
import { IPageElement } from '@signing-page/signing/types/page-element';

export class StopWatch {
  startTime: number;
  pauseTime: number;

  start() {
    if (this.startTime) {
      throw Error('Can not start a stopwatch that is already started');
    }
    this.startTime = new Date().getTime();
  }

  stop(): number {
    if (!this.startTime) {
      throw Error('Can not stop a stopwatch that is not started');
    }
    const currentTime: number = new Date().getTime();
    let timeElapsed: number = currentTime - this.startTime;
    if (this.pauseTime) {
      timeElapsed -= currentTime - this.pauseTime;
    }
    this.startTime = null;
    this.pauseTime = null;
    return timeElapsed;
  }

  pause() {
    if (!this.startTime) {
      throw Error('Can not pause a stopwatch that is not started');
    }
    if (this.pauseTime) {
      throw Error('Can not pause a stopwatch that is already paused');
    }
    this.pauseTime = new Date().getTime();
  }

  resume() {
    if (!this.startTime) {
      throw Error('Can not resume a stopwatch that is not started');
    }
    if (!this.pauseTime) {
      throw Error('Can not resume a stopwatch that is not paused');
    }

    this.startTime -= this.pauseTime - new Date().getTime();
    this.pauseTime = null;
  }
}

export class TrackingHelper {
  static allowedTrackingRecipientStatus(status: ERecipientStatus): boolean {
    const forbiddenStatuses: ERecipientStatus[] = [
      ERecipientStatus.Signed,
      ERecipientStatus.Approved,
      ERecipientStatus.Rejected,
    ];
    return !forbiddenStatuses.includes(status);
  }

  static shouldSendViewedPush(documentRecipients: IRecipient[], recipientId: string): boolean {
    const recipient: IRecipient = documentRecipients.find(
      (recipient: IRecipient) => recipient.id === recipientId
    );
    return [ERecipientStatus.Added, ERecipientStatus.Sent].includes(recipient?.status);
  }
}

export function isPageInFocus(page: IPageElement): boolean {
  const positionTop: number = page.element.getBoundingClientRect().top - topBarHeight;
  const positionBottom: number = page.element.getBoundingClientRect().bottom - topBarHeight;

  const pageHeight: number = page.element.clientHeight;
  const percent: number = pageHeight / getContentTrackingPercent(page);

  return positionBottom > percent || positionTop > percent;
}

export function getContentTrackingPercent(page: IPageElement): number {
  const { type } = page;
  switch (type) {
    case EDocumentPageType.Editor:
    case EDocumentPageType.DocumentContent:
      return 10;
    case EDocumentPageType.Video:
    case EDocumentPageType.ContentLink:
      return 80;
    default:
      return 50;
  }
}
